import {Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Data} from '@angular/router';
import {TranslocoService} from '@ngneat/transloco';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {AbstractComponent, ConfirmationModalComponent, NavTabsComponent} from "@components";
import {
  ADMINISTRATOR_ROLE,
  Country,
  GroupScoringConfiguration,
  IndividualScoringConfiguration,
  League,
  LeagueDetailsComponentType,
  LeagueStatus,
  Lookups,
  Organiser,
  ScoringCategorization,
  ScoringDefinition,
  VisibilityStatus,
  TOAST_STATE, ORGANISERS_ADMINISTRATOR_ROLE, LeagueEvents,
} from "@model";
import {
  AuthenticationService,
  EventService,
  LeagueScoringService,
  LeagueService,
  RequestService,
  ToastService,
} from "@services";
import {isLeagueBasicDataValid, isScoringCategorizationValid} from "@utility";
import {forkJoin} from "rxjs";

@Component({
    selector: 'app-admin-league-details',
    templateUrl: './admin-league-details.component.html',
    styleUrls: ['./admin-league-details.component.scss'],
    animations: [
        trigger('fadeInAnimation', [
            state('in', style({ opacity: 1 })),
            transition(':enter', [
                style({ opacity: 0 }),
                animate(1600)
            ])
        ])
    ],
    standalone: false
})

export class AdminLeagueDetailsComponent extends AbstractComponent implements OnInit, OnDestroy {
  @Input() league: League | undefined;
  @Input() leagueEvents: LeagueEvents | undefined;
  @Input() organisers: Organiser[] = [];
  @Input() userRole: String | undefined;
  @Input({required: true}) sports: Lookups[] = [];
  @Input({required: true}) countries: Country[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() scoringMethods: Lookups[] = [];
  @Input() rankingMethods: Lookups[] = [];
  @Input() measurementPrecisionTypes: Lookups[] = [];
  @Input() attendanceBonusTypes: Lookups[] = [];
  @Input() groupScoringTypes: Lookups[] = [];
  @ViewChild("nav") nav?: NavTabsComponent;

  currentLeague: League | undefined;
  componentType: LeagueDetailsComponentType = LeagueDetailsComponentType.ADD
  isLeagueInvalid: boolean = true;
  isLeagueEventsValid: boolean = true;
  isScoringConfigValid: boolean = false;
  isScoringCategorizationValid: boolean = false;

  individualScoringConfigurations: IndividualScoringConfiguration[] = [];
  groupScoringConfigurations: GroupScoringConfiguration[] = [];
  scoringCategorizations: ScoringCategorization[] = [];
  scoringDefinitions: ScoringDefinition[] = [];

  editingDisabled: boolean = true;
  isAdministrator: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private toastService: ToastService,
    public translocoService: TranslocoService,
    private eventService: EventService,
    private leagueService: LeagueService,
    private modalService: NgbModal,
    private authService: AuthenticationService,
    private leagueScoringService : LeagueScoringService,
    private requestService : RequestService
  ) {
    super();
  }

  ngOnInit() {
    this.subscribeToRouteDataChange();
    this.initData()
    this.isAdministrator = this.userRole === ADMINISTRATOR_ROLE || this.userRole === ORGANISERS_ADMINISTRATOR_ROLE;
    this.subscriptions.push(this.leagueService.currentLeague.subscribe(value => {
      this.currentLeague = value;
      this.initData();
    }))
  }


  initData() {
    this.isLeagueInvalid = !isLeagueBasicDataValid(this.currentLeague).isValid;
    this.validateLeagueEvents();
    this.initLists();
    this.editingDisabled =
        this.currentLeague?.visibilityStatus === VisibilityStatus.VISIBLE ||
        this.currentLeague?.visibilityStatus === VisibilityStatus.REQUEST_SENT;
  }



  initLists() {
    this.scoringCategorizations = this.currentLeague?.scoringCategorizations?.length ? this.currentLeague.scoringCategorizations! : [];
    this.scoringDefinitions = this.currentLeague?.scoringDefinitions?.length ? this.currentLeague.scoringDefinitions! : [];
    this.individualScoringConfigurations = this.currentLeague?.individualScoringConfigurations?.length ? this.currentLeague.individualScoringConfigurations! : [];
    this.groupScoringConfigurations = this.currentLeague?.groupScoringConfigurations?.length ? this.currentLeague.groupScoringConfigurations! : [];
    this.validateScoringCategorizations();
    this.validateScoringConfiguration()
  }

  leagueChange(league: League | undefined) {
    this.currentLeague = league;
    this.isLeagueInvalid = !isLeagueBasicDataValid(this.currentLeague).isValid;
    this.onScoringCategorizationChange();
    this.onScoringConfigurationChange();
    this.validateLeagueEvents();
  }


  validateLeagueEvents(): void {
    const leagueStart = this.currentLeague?.start ? new Date(this.currentLeague.start) : null;
    const leagueEnd = this.currentLeague?.end ? new Date(this.currentLeague.end) : null;

    const hasEvents = (this.leagueEvents?.events?.length ?? 0) > 0 ||
        (this.leagueEvents?.templateEvents?.length ?? 0) > 0;

    const allInRange = [...(this.leagueEvents?.events ?? []),
      ...(this.leagueEvents?.templateEvents ?? []),
      ...(this.leagueEvents?.pendingEvents ?? [])]
        .every(e => {
          const start = e.start ? new Date(e.start) : null;
          const end = e.end ? new Date(e.end) : null;
          return (!start || !leagueStart || start >= leagueStart) &&
              (!end || !leagueEnd || end <= leagueEnd);
        });

    this.isLeagueEventsValid = hasEvents && allInRange;
  }

  leagueEventsChanged(leagueEvents: LeagueEvents | undefined) {
    this.leagueEvents = leagueEvents;
    this.onScoringCategorizationChange();
    this.onScoringConfigurationChange();
    this.validateLeagueEvents();
  }

  validateScoringCategorizations() {
    this.isScoringCategorizationValid = this.scoringCategorizations.length > 0;
    this.scoringCategorizations.forEach(c => {
      if (!isScoringCategorizationValid(c, false).isValid) {
        this.isScoringCategorizationValid = false;
        return;
      }
    });
  }

  validateScoringConfiguration() {
    this.isScoringConfigValid = ((this.scoringDefinitions.length > 0) && (this.individualScoringConfigurations.length > 0) && (this.groupScoringConfigurations.length > 0))
  }

  private subscribeToRouteDataChange(): void {
    this.subscriptions.push(
      this.route.data.subscribe(({type, league}: Data): void => {
        this.componentType = type ?? LeagueDetailsComponentType.ADD;
        if (league) {
          this.currentLeague = league;
        } else {
          this.currentLeague = new League();
          this.currentLeague.status = LeagueStatus.INACTIVE;
          this.componentType = LeagueDetailsComponentType.ADD
        }
        this.isLeagueInvalid = !isLeagueBasicDataValid(this.currentLeague).isValid;
        this.leagueService.setCurrentLeague(this.currentLeague);
        this.initLists();
        this.validateLeagueEvents();
      })
    )
  }


  disableEditing(): boolean {
    return this.isLeagueInvalid;
  }


  onScoringCategorizationChange() {
    if (!this.league?.id) return;
    this.leagueScoringService.getScoringCategorizationsByLeagueId(this.league!.id!).subscribe(value => {
        this.scoringCategorizations = value;
        this.validateScoringCategorizations()
      })
  }

  onScoringConfigurationChange() {
    if (!this.league?.id) return;

    const individualRequest = this.leagueScoringService.getIndividualScoringConfigurationsByLeagueId(this.league.id);
    const groupRequest = this.leagueScoringService.getGroupScoringConfigurationsByLeagueId(this.league.id);
    const scoringDefinitionsRequest = this.leagueScoringService.getScoringDefinitionsByLeagueId(this.league.id);
    forkJoin([
      individualRequest, groupRequest, scoringDefinitionsRequest]).subscribe({
      next: ([individualScoringConfigurations,
               groupScoringConfigurations,
               scoringDefinitions]: [IndividualScoringConfiguration[], GroupScoringConfiguration[], ScoringDefinition[]]) => {
        this.individualScoringConfigurations = [...individualScoringConfigurations];
        this.groupScoringConfigurations = [...groupScoringConfigurations];
        this.scoringDefinitions = [...scoringDefinitions];
        this.validateScoringConfiguration();
      },
      error: (err) => {
      }
    });
  }

  navigateToLeagueBasicInfo(): void {
    this.nav?.select(1);
  }

  onEnableEditingClick() {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
    });
    modalRef.componentInstance.title = this.translocoService.translate('league.details.enableEditing');
    modalRef.componentInstance.text = this.translocoService.translate(this.isAdministrator ?
        'league.details.enableEditing.modal.admin.text' :
        'league.details.enableEditing.modal.organiser.text');

    modalRef.result.then((_) => {
      this.enableEditing();
    }, (_) => {
      /// ignore
    });
  }

  enableEditing() {
    if (this.currentLeague) {
      this.leagueService.hideLeague(this.currentLeague!, () => {
        this.leagueService.currentLeague.next({...this.currentLeague, visibilityStatus: VisibilityStatus.HIDDEN})
      }, () => {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate("app.common.error"));
      });
    }
  }


  openModifyRequestDialog(accept: boolean) {
      const modalRef: NgbModalRef = this.modalService.open(ConfirmationModalComponent, {
        centered: true,
      });
      let requestComment: string = "";
      modalRef.componentInstance.title = this.translocoService.translate(accept ? "request.list.accept.dialog.title" : "request.list.decline.dialog.title");
      modalRef.componentInstance.text = this.translocoService.translate(accept ? "request.list.accept.dialog.text" : "request.list.decline.dialog.text");

      if (!accept) {
        modalRef.componentInstance.dispayTextArea = true;
        modalRef.componentInstance.textAreaPlaceholder = this.translocoService.translate("request.action.add.comment");
        modalRef.componentInstance.textAreInputEvent.subscribe((content: string) => requestComment = content);
      }

      modalRef.result.then((_) => {
        this.onModifyRequest(accept, requestComment !== "" ? requestComment : undefined);
      }, (_) => {
        /// ignore
      });
  }


  onModifyRequest(accept: boolean, comment: string | undefined) {
    this.requestService.managePublishLeagueRequestByLeague(this.currentLeague!, accept, comment).subscribe(value => {
      if (value) {
        this.leagueService.currentLeague.next({...this.currentLeague, visibilityStatus: accept ? VisibilityStatus.VISIBLE : VisibilityStatus.HIDDEN})
        this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('request.modify.success'));
      }
      else {
        this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate('request.modify.error'));
      }
    })
  }

  protected readonly VisibilityStatus = VisibilityStatus;

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.leagueService.currentLeague.next(undefined);
  }
}
